import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { catchError, lastValueFrom } from "rxjs";
import { HttpUserService } from "../../../services/user/http-user.service";
import { SpinnerService } from "../../../components/spinner/spinner.service";
import { ConfirmService } from "../../../components/confirm-dialog/confirm-dialog.service";
import { UserDto, UserStatusEnum } from "../../../dto";

@Injectable({
	providedIn: 'root'
})
export class UserActionService {

	constructor(
		private httpUserService: HttpUserService,
		private toastr: ToastrService,
		private spinnerService: SpinnerService,
		private confirmService: ConfirmService
	) { }

	async disableUser(input: UserDto): Promise<boolean> {
		const confirmed = await this.confirmService.show({
			title: 'Disable User',
			text: `Are you sure you wish to disable: ${input.username}?`,
			subText: `${input.name} ${input.surname} will no longer be able to use the system.`,
			yesText: 'Disable',
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		// Update status
		const payload = { ...input, status: UserStatusEnum.Disabled };

		this.spinnerService.show('Disabling user...');
		try {
			const result = await lastValueFrom(
				this.httpUserService.userAction(payload, 'disable').pipe(
					catchError(err => {
						console.error('Error disabling user', err);
						this.toastr.error('Failed to disable user: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('User disabled successfully');
				return true;
			} else {
				this.toastr.error('Failed to disable user');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

	async enableOrUnblockUser(input: UserDto, type: 'unblock' | 'enable'): Promise<boolean> {
		var actionLower = type === 'enable' ? 'enable' : 'unblock';
		var actionUpper = type === 'enable' ? 'Enable' : 'Unblock';
		var actionVerb = type === 'enable' ? 'Enabling' : 'Unblocking';
		var actionPastTense = type === 'enable' ? 'enabled' : 'unblocked';
		const confirmed = await this.confirmService.show({
			title: `${actionUpper} User`,
			text: `Are you sure you wish to ${actionLower} user: ${input.username}?`,
			subText: `${input.name} ${input.surname} will have access to the system.`,
			yesText: `${actionUpper}`,
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		// Update status
		const payload = { ...input, status: UserStatusEnum.Active };

		this.spinnerService.show(actionVerb + ' user...');
		try {
			const result = await lastValueFrom(
				this.httpUserService.userAction(payload, type).pipe(
					catchError(err => {
						console.error('Error ' + actionVerb + ' user', err);
						this.toastr.error('Failed to ' + actionLower + ' user: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('User ' + actionPastTense + ' successfully');
				return true;
			} else {
				this.toastr.error('Failed to ' + actionLower + ' user');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

}